// Environment-specific API configuration
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.REACT_APP_SUPABASE_ANON_KEY;
const isSupabaseFunctions = BACKEND_URL && BACKEND_URL.includes('supabase.co');

// Mapping of API endpoints to Supabase Edge Function names
const SUPABASE_FUNCTION_MAP = {
  'dashboards': 'get-dashboards',
  'dashboards/featured': 'get-featured-dashboards',
  'dashboards/categories': 'get-dashboard-categories',
  'dashboards/tools': 'get-dashboard-tools',
  'dashboards/generate-metadata': 'generate-metadata',
  'dashboards/generate-description': 'generate-description',
  'auth/me': 'auth-me',
  'auth/login': 'auth-login',
  'auth/logout': 'auth-logout',
  'auth/signup': 'auth-signup',
  'auth/refresh': 'auth-refresh',
  'auth/change-password': 'auth-change-password',
  'upload/image': 'upload-dashboard-image',
  'upload/images': 'upload-dashboard-image',
  'blog-media-upload': 'blog-media-upload',
  'log-visitor': 'log-visitor',
  'get-visitor-analytics': 'get-visitor-analytics',
  'blog-ai-polish': 'blog-ai-polish',
  'polish-text': 'blog-ai-polish',
  'test-cloudinary': 'test-cloudinary'
};

// API configuration based on environment
export const getApiUrl = (endpoint, method = 'GET') => {
  // Validate endpoint parameter
  if (!endpoint || typeof endpoint !== 'string') {
    console.error('Invalid endpoint provided to getApiUrl:', endpoint);
    return `${BACKEND_URL || 'http://localhost:8000'}/api/`;
  }

  // If using Supabase functions (production), map to function names
  if (isSupabaseFunctions) {
    // Parse endpoint to separate base path from query parameters
    const [basePath, queryString] = endpoint.split('?');
    const queryParams = queryString ? `?${queryString}` : '';
    
    // Handle dynamic endpoints like dashboards/:id
    if (basePath.startsWith('dashboards/') && basePath.split('/').length > 1 && !basePath.includes('featured') && !basePath.includes('categories') && !basePath.includes('tools') && !basePath.includes('generate-metadata') && !basePath.includes('generate-description')) {
      const dashboardId = basePath.split('/')[1];
      // Route based on HTTP method
      if (method === 'PUT') {
        return `${BACKEND_URL}/update-dashboard/${dashboardId}${queryParams}`;
      } else {
        return `${BACKEND_URL}/get-dashboard/${dashboardId}${queryParams}`;
      }
    }
    
    // Handle dashboards endpoint with query parameters
    if (basePath === 'dashboards') {
      // Route based on HTTP method
      if (method === 'POST') {
        return `${BACKEND_URL}/create-dashboard${queryParams}`;
      } else {
        return `${BACKEND_URL}/get-dashboards${queryParams}`;
      }
    }
    
    // Handle delete-dashboard endpoint with ID
    if (basePath.startsWith('delete-dashboard/')) {
      const dashboardId = basePath.split('/')[1];
      return `${BACKEND_URL}/delete-dashboard/${dashboardId}${queryParams}`;
    }
    
    // Handle delete-dashboard endpoint without ID (fallback)
    if (basePath === 'delete-dashboard') {
      return `${BACKEND_URL}/delete-dashboard${queryParams}`;
    }
    
    // Handle update-dashboard endpoint
    if (basePath.includes('update-dashboard')) {
      return `${BACKEND_URL}/update-dashboard${queryParams}`;
    }
    
    // Handle create-dashboard endpoint
    if (basePath.includes('create-dashboard') || basePath === 'dashboards' && endpoint.method === 'POST') {
      return `${BACKEND_URL}/create-dashboard${queryParams}`;
    }
    
    // Handle upload endpoints with dynamic paths
    if (basePath.startsWith('upload/image/')) {
      return `${BACKEND_URL}/upload-dashboard-image${queryParams}`;
    }
    
    // Map standard endpoints to function names
    const functionName = SUPABASE_FUNCTION_MAP[basePath];
    if (functionName) {
      return `${BACKEND_URL}/${functionName}${queryParams}`;
    }
    
    // Fallback for unmapped endpoints
    console.warn(`Unmapped Supabase function endpoint: ${endpoint}`);
    return `${BACKEND_URL}/${basePath}${queryParams}`;
  }
  
  // For local development, use /api prefix with proper endpoint mapping
  const [basePath, queryString] = endpoint.split('?');
  const queryParams = queryString ? `?${queryString}` : '';
  
  // Map frontend endpoints to backend routes
  if (basePath === 'dashboards/featured') {
    return `${BACKEND_URL}/api/dashboards/featured${queryParams}`;
  }
  
  if (basePath === 'dashboards/categories') {
    return `${BACKEND_URL}/api/dashboards/categories${queryParams}`;
  }
  
  if (basePath === 'dashboards/tools') {
    return `${BACKEND_URL}/api/dashboards/tools${queryParams}`;
  }
  
  // Default mapping
  return `${BACKEND_URL}/api/${endpoint}`;
};

// Get headers for Supabase function requests
export const getSupabaseHeaders = (skipContentType = false) => {
  if (isSupabaseFunctions && SUPABASE_ANON_KEY) {
    const headers = {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    };
    
    // Don't set Content-Type for FormData requests - let browser handle it
    if (!skipContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }
  
  const headers = {};
  // Don't set Content-Type for FormData requests - let browser handle it
  if (!skipContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

// Legacy API constant for backward compatibility
export const API = `${BACKEND_URL}/api`;
export const BACKEND_URL_EXPORT = BACKEND_URL;